import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './list.module.scss';
import { focusIfHTMLElement, setAttributeIfElement } from '@/utils';

//contants
import { KEY } from 'Data/constants';

interface A11yListProps {
  children?: React.ReactNode;
  ariaLabel?: string;
  focusStyle?: string;
  id: string;
  labelledby?: string;
  listStyle?: string;
  loop?: boolean;
  onKeyUp?: React.KeyboardEventHandler<HTMLUListElement>;
  rememberLast?: boolean;
  skipFocus?: boolean;
  trapFocus?: boolean;
  onBlur?: React.FocusEventHandler<HTMLUListElement>;
  role?: string;
  forwardedRef?: React.ForwardedRef<HTMLUListElement> | null;
}

export const A11yList: React.FC<A11yListProps> = ({
  children,
  id,
  ariaLabel,
  listStyle,
  onKeyUp,
  loop = true,
  skipFocus = false,
  onBlur,
  role = 'listbox',
  trapFocus = false,
  forwardedRef,
}) => {
  const listBoxNode = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const optionsWrapper = listBoxNode.current;

    const handleKeyboardEvents = (e: KeyboardEvent) => {
      const actionKeysObject: { [key: string]: () => void } = {
        [KEY.ARROW_DOWN]: () => {
          e.preventDefault();

          if (e.target instanceof HTMLElement) {
            const next = e.target?.nextSibling ?? listBoxNode.current?.firstChild;

            focusIfHTMLElement(next);

            if (loop) {
              setAttributeIfElement(next, 'tabindex', '-1');

              next?.nextSibling
                ? setAttributeIfElement(next.nextSibling, 'tabindex', '0')
                : setAttributeIfElement(listBoxNode.current?.firstChild, 'tabindex', '0');
            }
          }
        },
        [KEY.ARROW_UP]: () => {
          e.preventDefault();

          if (e.target instanceof HTMLElement) {
            const prev = e.target?.previousSibling ?? listBoxNode.current?.lastChild;

            focusIfHTMLElement(prev);

            if (loop) {
              setAttributeIfElement(prev, 'tabindex', '-1');

              prev?.previousSibling
                ? setAttributeIfElement(prev.previousSibling, 'tabindex', '0')
                : setAttributeIfElement(listBoxNode.current?.lastChild, 'tabindex', '-1');
            }
          }
        },
        [KEY.HOME]: () => {
          e.preventDefault();

          if (loop && e.target instanceof HTMLElement) {
            focusIfHTMLElement(listBoxNode.current?.firstChild);

            setAttributeIfElement(listBoxNode.current?.firstChild, 'tabindex', '0');
            e.target.setAttribute('tabindex', '-1');
          }
        },
        [KEY.END]: () => {
          e.preventDefault();

          if (e.target instanceof HTMLElement) {
            focusIfHTMLElement(listBoxNode.current?.lastChild);

            if (loop) {
              setAttributeIfElement(listBoxNode.current?.lastChild, 'tabindex', '0');
              e.target.setAttribute('tabindex', '-1');
            }
          }
        },
        [KEY.TAB]: () => {
          if (trapFocus) {
            if (
              !e.shiftKey &&
              document.activeElement === (listBoxNode.current?.lastChild as Element | null)?.children?.[0]
            ) {
              // Go back to top if at bottom
              e.preventDefault();
              focusIfHTMLElement(listBoxNode.current?.firstChild);

              if (loop) {
                setAttributeIfElement(listBoxNode.current?.firstChild, 'tabindex', '0');
                listBoxNode.current?.setAttribute('tabindex', '-1');
              }
            }

            if (
              e.shiftKey &&
              document.activeElement === (listBoxNode.current?.firstChild as Element | null)?.children?.[0]
            ) {
              // Go back to bottom if at
              e.preventDefault();
              focusIfHTMLElement(listBoxNode.current?.lastChild);

              if (loop) {
                setAttributeIfElement(listBoxNode.current?.lastChild, 'tabindex', '0');
                listBoxNode.current?.setAttribute('tabindex', '-1');
              }
            }
          }
        },
      };

      actionKeysObject[e.key]?.();
    };

    if (!optionsWrapper) return;

    optionsWrapper.addEventListener(KEY.DOWN, handleKeyboardEvents);

    if (forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(optionsWrapper);
      } else {
        forwardedRef.current = optionsWrapper;
      }
    }

    return () => {
      optionsWrapper.removeEventListener(KEY.DOWN, handleKeyboardEvents);
    };
  }, [loop, trapFocus, forwardedRef]);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <ul
      data-testid={id}
      ref={listBoxNode}
      tabIndex={skipFocus ? -1 : 0}
      role={role}
      aria-label={ariaLabel}
      aria-activedescendant=""
      aria-multiselectable={role === 'listbox' ? 'true' : 'false'}
      className={clsx(styles.listBox, listStyle)}
      onKeyUp={onKeyUp}
      onBlur={onBlur}
    >
      {children}
    </ul>
  );
};
